import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useContext, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { getCopilotStreamAnswer, INTERACTION_TYPES, SESSION_TYPES } from "../../../lib/services/copilot";
import { useWeb5Context } from "../../../lib/Web5Context";
import copyToClipboard from "../../../utils/copy";
import { CopilotContext } from "../Context";
import AgentSwitcher from "./components/AgentSwitcher";
import Chat from "./components/Chat";
import ChatToolbar from "./components/ChatToolbar";
import useBuildAnswer from "../hooks/useBuildAnswer";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CopilotChat');
const CopilotChat = /*#__PURE__*/memo(_ref => {
  let {
    chatId,
    chatSuggestions,
    className,
    locale,
    handleEmail,
    handleFeedback,
    handleVote,
    session,
    interactions = [],
    defaultFirstInteraction,
    cb,
    // Callbacks for the Copilot chat events
    overrides = {}
  } = _ref;
  const classes = useStyles();
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);
  const answerInteractionIdRef = useRef(null);
  const contentRef = useRef(null);
  const questionRef = useRef('');
  const answerRef = useRef('');
  const scrollTimeoutRef = useRef(null);
  const {
    t
  } = useWeb5Context();
  const {
    cleanChatTextForClipboard
  } = useBuildAnswer();
  const {
    setInteraction,
    clearChat,
    setChatSession,
    setIsInitialized,
    setStreamReader,
    getChatState,
    getMethodsForCopilot
  } = useContext(CopilotContext);
  const chatState = useMemo(() => getChatState({
    chatId
  }), [getChatState, chatId]);
  const {
    chat,
    chatSession,
    isInitialized,
    reports,
    tables
  } = chatState;
  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      const el = contentRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  // Debounced version to prevent multiple rapid scrolls
  const debouncedScrollToBottom = useCallback(function (delay) {
    if (delay === void 0) {
      delay = 100;
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(scrollToBottom, delay);
  }, [scrollToBottom]);
  const cancelSequence = useCallback(() => {
    setStreamReader({
      chatId,
      reader: null
    });
  }, [chatId, setStreamReader]);
  const onAnswer = useCallback(_ref2 => {
    let {
      id,
      responseId,
      text
    } = _ref2;
    if (text) {
      var _cb$onAnswer;
      // To keep track of the previous chunk of text
      answerRef.current = text;
      setInteraction({
        chatId,
        id: answerInteractionIdRef.current,
        responseId,
        text
      });
      cb === null || cb === void 0 ? void 0 : (_cb$onAnswer = cb.onAnswer) === null || _cb$onAnswer === void 0 ? void 0 : _cb$onAnswer.call(cb, {
        id,
        responseId,
        text
      });
      debouncedScrollToBottom(10);
    }
  }, [cb, chatId, debouncedScrollToBottom, setInteraction]);
  const onData = useCallback(data => {
    var _cb$onData;
    cb === null || cb === void 0 ? void 0 : (_cb$onData = cb.onData) === null || _cb$onData === void 0 ? void 0 : _cb$onData.call(cb, data);
  }, [cb]);
  const onError = useCallback(parentId => () => {
    var _cb$onError;
    setStreamReader({
      chatId,
      reader: null
    });
    setInteraction({
      chatId,
      id: answerInteractionIdRef.current,
      isError: true,
      parentId
    });
    answerRef.current = '';
    answerInteractionIdRef.current = null;
    cb === null || cb === void 0 ? void 0 : (_cb$onError = cb.onError) === null || _cb$onError === void 0 ? void 0 : _cb$onError.call(cb, parentId);
  }, [cb, chatId, setInteraction, setStreamReader]);
  const onFinish = useCallback(_ref3 => {
    var _cb$onFinish;
    let {
      id,
      text
    } = _ref3;
    answerRef.current = '';
    setIsLoadingAnswer(false);
    setStreamReader({
      chatId,
      reader: null
    });
    setInteraction({
      chatId,
      id: answerInteractionIdRef.current,
      text,
      isFinished: true
    });
    answerInteractionIdRef.current = null;
    cb === null || cb === void 0 ? void 0 : (_cb$onFinish = cb.onFinish) === null || _cb$onFinish === void 0 ? void 0 : _cb$onFinish.call(cb, {
      id,
      text
    });

    // Scroll to bottom after content is finished loading
    debouncedScrollToBottom();
  }, [setStreamReader, chatId, setInteraction, cb, debouncedScrollToBottom]);
  const onAction = useCallback(action => {
    var _cb$onAction;
    setInteraction({
      chatId,
      id: answerInteractionIdRef.current,
      action
    });
    cb === null || cb === void 0 ? void 0 : (_cb$onAction = cb.onAction) === null || _cb$onAction === void 0 ? void 0 : _cb$onAction.call(cb, action);
  }, [cb, chatId, setInteraction]);
  const onArtifact = useCallback(_ref4 => {
    var _cb$onArtifact;
    let {
      type,
      payload
    } = _ref4;
    setInteraction({
      chatId,
      id: answerInteractionIdRef.current,
      [type]: payload
    });
    cb === null || cb === void 0 ? void 0 : (_cb$onArtifact = cb.onArtifact) === null || _cb$onArtifact === void 0 ? void 0 : _cb$onArtifact.call(cb, {
      type,
      payload
    });
  }, [chatId, setInteraction, cb]);
  const onSetChatSession = useCallback(session => setChatSession({
    chatId,
    session
  }), [chatId, setChatSession]);
  const onSubmitChat = useCallback(suggestion => {
    const question = suggestion || questionRef.current || '';
    cancelSequence();
    setIsLoadingAnswer(true);
    const questionId = uuid();
    answerInteractionIdRef.current = uuid();
    setInteraction({
      chatId,
      text: question,
      id: questionId,
      isQuestion: true
    });
    setInteraction({
      chatId,
      id: answerInteractionIdRef.current,
      text: '',
      isLoading: true,
      parentId: questionId
    });

    // Scroll to bottom after setting up the interaction
    debouncedScrollToBottom();
    return getCopilotStreamAnswer({
      session: _objectSpread(_objectSpread({}, session), {}, {
        id: chatSession === null || chatSession === void 0 ? void 0 : chatSession.id,
        type: !!(chatSession !== null && chatSession !== void 0 && chatSession.id) ? SESSION_TYPES.UPDATE : SESSION_TYPES.CREATE
      }),
      interactions: [...interactions, {
        item: {
          type: INTERACTION_TYPES.MESSAGE,
          content: {
            type: 'input_text',
            text: question
          }
        }
      }],
      setText: onAnswer,
      setData: onData,
      setSession: onSetChatSession,
      onFinish,
      onError: onError(questionId),
      onAction,
      onSetReader: reader => setStreamReader({
        chatId,
        reader
      }),
      onArtifact
    });
  }, [cancelSequence, chatId, setInteraction, session, chatSession === null || chatSession === void 0 ? void 0 : chatSession.id, interactions, onAnswer, onData, onSetChatSession, onFinish, onError, onAction, onArtifact, setStreamReader, setIsLoadingAnswer, debouncedScrollToBottom]);
  const handleClearChat = useCallback(() => {
    cancelSequence();
    clearChat({
      chatId
    });
  }, [cancelSequence, clearChat, chatId]);
  const getContentRef = ref => {
    if (ref) {
      contentRef.current = ref;
    }
  };
  const getQuestionRef = useCallback(question => {
    questionRef.current = question;
  }, []);
  const isDanaWritting = useMemo(() => {
    return isLoadingAnswer;
  }, [isLoadingAnswer]);
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized({
        chatId,
        value: true
      });
      if (defaultFirstInteraction !== null && defaultFirstInteraction !== void 0 && defaultFirstInteraction.text) {
        setInteraction(_objectSpread({
          chatId,
          id: uuid(),
          isFinished: true,
          isQuestion: false,
          isError: false,
          isLoading: false,
          disableVote: true
        }, defaultFirstInteraction));
        // Scroll to bottom after setting default interaction
        debouncedScrollToBottom();
      }
      if (interactions.length > 0 && !answerInteractionIdRef.current) {
        setIsLoadingAnswer(true);
        const questionId = uuid();
        answerInteractionIdRef.current = uuid();

        // Handle GoldenMinute click on CTA prompts
        interactions.filter(_ref5 => {
          let {
            item
          } = _ref5;
          return item.type === INTERACTION_TYPES.MESSAGE;
        }).forEach(_ref6 => {
          let {
            item
          } = _ref6;
          setInteraction({
            chatId,
            id: uuid(),
            text: item.content.text,
            isFinished: true,
            isQuestion: true,
            isError: false,
            isLoading: false,
            parentId: questionId
          });
        });
        setInteraction({
          chatId,
          id: answerInteractionIdRef.current,
          text: '',
          isLoading: true,
          parentId: questionId
        });
        getCopilotStreamAnswer({
          session,
          interactions,
          setText: onAnswer,
          setData: onData,
          setSession: onSetChatSession,
          onFinish,
          onError: onError(questionId),
          onAction,
          onArtifact,
          onSetReader: reader => setStreamReader({
            chatId,
            reader
          })
        }).finally(() => {
          setIsLoadingAnswer(false);
          // Scroll to bottom after initial content loads
          debouncedScrollToBottom();
        });
      }
    }
  }, [chatId, setInteraction, defaultFirstInteraction, onAnswer, onData, onFinish, onError, session, interactions, onSetChatSession, isInitialized, setIsInitialized, onAction, onArtifact, setStreamReader, debouncedScrollToBottom]);
  useEffect(() => {
    getMethodsForCopilot.registerFeatureMethods(`chat-${chatId}`, {
      onSubmit: onSubmitChat
    });
  }, [getMethodsForCopilot, onSubmitChat, chatId]);
  useEffect(() => {
    debouncedScrollToBottom();
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [debouncedScrollToBottom]);
  const onCopy = useCallback(value => {
    if (!value.content) return;
    const text = cleanChatTextForClipboard({
      value: value.content,
      tables,
      reports
    });
    copyToClipboard(text, t);
  }, [cleanChatTextForClipboard, reports, t, tables]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(classes.root, className)
  }, chat && /*#__PURE__*/React.createElement("div", {
    className: classes.chat,
    ref: ref => getContentRef(ref)
  }, /*#__PURE__*/React.createElement(Chat, {
    chatId: chatId,
    conversationId: chatSession === null || chatSession === void 0 ? void 0 : chatSession.id,
    conversation: chat,
    chatSuggestions: chatSuggestions,
    isDanaWritting: isDanaWritting,
    onCopy: onCopy,
    onEmail: handleEmail,
    onFeedback: handleFeedback,
    onVote: handleVote
  })), !(overrides !== null && overrides !== void 0 && overrides.disableChatInput) && /*#__PURE__*/React.createElement("div", {
    className: classes.chatBar
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.chatToolbar
  }, !(overrides !== null && overrides !== void 0 && overrides.disableAgentSwitcher) && /*#__PURE__*/React.createElement(AgentSwitcher, {
    chatId: chatId
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.input
  }, /*#__PURE__*/React.createElement(ChatToolbar, {
    getQuestionRef: getQuestionRef,
    onSubmit: onSubmitChat,
    onClear: handleClearChat,
    conversation: chat,
    isLoadingAnswer: isLoadingAnswer,
    isDanaWritting: isDanaWritting,
    locale: locale
  }))));
});
export default CopilotChat;
export { getCopilotStreamAnswer, ChatToolbar };